import { useCallback, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import { DocumentCreateDialog } from "@/pages/dokumente/DocumentCreateDialog";
import {
  DocumentCreateContext,
  type DocumentCreateContextValue,
  type DocumentCreateCreatedHandler,
  type DocumentCreateOpenOptions,
} from "@/pages/dokumente/documentCreateContextValue";
import { useDocumentEditor } from "@/pages/dokumente/useDocumentEditor";

export function DocumentCreateProvider({ children }: { children: ReactNode }) {
  const { open: openEditor } = useDocumentEditor();
  const [isOpen, setIsOpen] = useState(false);
  const [dialogOptions, setDialogOptions] = useState<DocumentCreateOpenOptions>({});
  const oneShotCreatedRef = useRef<DocumentCreateCreatedHandler | null>(null);

  const close = useCallback(() => {
    setIsOpen(false);
    oneShotCreatedRef.current = null;
    setDialogOptions({});
  }, []);

  const open = useCallback((options: DocumentCreateOpenOptions = {}) => {
    oneShotCreatedRef.current = options.onCreated ?? null;
    setDialogOptions(options);
    setIsOpen(true);
  }, []);

  const handleCreated = useCallback(
    (docId: number) => {
      if (oneShotCreatedRef.current) {
        oneShotCreatedRef.current(docId);
        oneShotCreatedRef.current = null;
      } else {
        openEditor({ docId });
      }
      close();
    },
    [close, openEditor],
  );

  const value = useMemo<DocumentCreateContextValue>(
    () => ({ open, close, isOpen }),
    [open, close, isOpen],
  );

  return (
    <DocumentCreateContext.Provider value={value}>
      {children}
      <DocumentCreateDialog
        open={isOpen}
        onClose={close}
        onCreated={handleCreated}
        initialAuftragId={dialogOptions.auftragId}
        initialTyp={dialogOptions.typ}
        initialQuellDokumentId={dialogOptions.quellDokumentId}
      />
    </DocumentCreateContext.Provider>
  );
}
